import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * FeatureTour - Interactive tour highlighting key features with tooltips (SAT-85)
 *
 * Features:
 * - Highlights UI elements with spotlight effect
 * - Tooltips point to target elements
 * - Step navigation (next/previous/skip)
 * - Remembers completed state in localStorage
 * - Can be started from help menu or settings
 */

// Tour step definitions
const TOUR_STEPS = [
  {
    target: '[data-tour="new-chat"]',
    title: 'Start a New Chat',
    content: 'Click here to begin a fresh conversation with DP. Each conversation is saved automatically.',
    position: 'right'
  },
  {
    target: '[data-tour="project-selector"]',
    title: 'Organize with Projects',
    content: 'Create projects to group related conversations. Each project can have its own custom instructions.',
    position: 'right'
  },
  {
    target: '[data-tour="search-conversations"]',
    title: 'Search Conversations',
    content: 'Quickly find past conversations by searching through titles and content.',
    position: 'right'
  },
  {
    target: '[data-tour="folders"]',
    title: 'Folder Organization',
    content: 'Create folders to organize your conversations. Drag and drop to move conversations between folders.',
    position: 'right'
  },
  {
    target: '[data-tour="model-selector"]',
    title: 'Choose Your Model',
    content: 'Select different AI models based on your needs. Sonnet is fast and capable, while Opus excels at complex tasks.',
    position: 'top'
  },
  {
    target: '[data-tour="message-input"]',
    title: 'Chat with DP',
    content: 'Type your message here. Press Enter to send, or Shift+Enter for a new line. You can also attach images!',
    position: 'top'
  },
  {
    target: '[data-tour="settings"]',
    title: 'Customize Your Experience',
    content: 'Access settings to change theme, font size, custom instructions, and more.',
    position: 'right'
  }
];

const STORAGE_KEY = 'hasCompletedFeatureTour';

/**
 * Tooltip component that positions itself relative to target element
 */
function TourTooltip({ step, targetRect, onNext, onPrevious, onSkip, currentStep, totalSteps }) {
  const tooltipRef = useRef(null);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [arrowStyle, setArrowStyle] = useState({});

  useEffect(() => {
    if (!targetRect || !tooltipRef.current) return;

    const tooltip = tooltipRef.current;
    const tooltipRect = tooltip.getBoundingClientRect();
    const padding = 16;
    const arrowSize = 8;

    let top, left;
    let arrowTop, arrowLeft;
    let arrowClass = '';

    switch (step.position) {
      case 'right':
        top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
        left = targetRect.right + padding;
        arrowTop = '50%';
        arrowLeft = `-${arrowSize}px`;
        arrowClass = 'border-r-white dark:border-r-gray-800';
        break;
      case 'left':
        top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
        left = targetRect.left - tooltipRect.width - padding;
        arrowTop = '50%';
        arrowLeft = `${tooltipRect.width}px`;
        arrowClass = 'border-l-white dark:border-l-gray-800';
        break;
      case 'top':
        top = targetRect.top - tooltipRect.height - padding;
        left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
        arrowTop = `${tooltipRect.height}px`;
        arrowLeft = '50%';
        arrowClass = 'border-t-white dark:border-t-gray-800';
        break;
      case 'bottom':
      default:
        top = targetRect.bottom + padding;
        left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
        arrowTop = `-${arrowSize}px`;
        arrowLeft = '50%';
        arrowClass = 'border-b-white dark:border-b-gray-800';
        break;
    }

    // Keep tooltip within viewport
    const viewportPadding = 16;
    if (left < viewportPadding) left = viewportPadding;
    if (left + tooltipRect.width > window.innerWidth - viewportPadding) {
      left = window.innerWidth - tooltipRect.width - viewportPadding;
    }
    if (top < viewportPadding) top = viewportPadding;
    if (top + tooltipRect.height > window.innerHeight - viewportPadding) {
      top = window.innerHeight - tooltipRect.height - viewportPadding;
    }

    setTooltipStyle({ top: `${top}px`, left: `${left}px` });
    setArrowStyle({ top: arrowTop, left: arrowLeft, className: arrowClass });
  }, [targetRect, step.position]);

  return (
    <div
      ref={tooltipRef}
      className="fixed z-10001 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-fadeIn"
      style={tooltipStyle}
    >
      {/* Progress bar */}
      <div className="h-1 bg-gray-200 dark:bg-gray-700">
        <div
          className="h-full bg-decisiv-blue transition-all duration-300"
          style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
        />
      </div>

      <div className="p-4">
        {/* Step indicator */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-decisiv-blue">
            Step {currentStep + 1} of {totalSteps}
          </span>
          <div className="flex gap-1">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  i === currentStep
                    ? 'bg-decisiv-blue'
                    : i < currentStep
                      ? 'bg-decisiv-blue/50'
                      : 'bg-gray-300 dark:bg-gray-600'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {step.title}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {step.content}
        </p>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={onSkip}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <button
                onClick={onPrevious}
                className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Back
              </button>
            )}
            <button
              onClick={onNext}
              className="px-4 py-1.5 text-sm bg-decisiv-blue text-white rounded-lg hover:bg-decisiv-blue/90 transition-colors font-medium"
            >
              {currentStep === totalSteps - 1 ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Spotlight overlay that highlights the target element
 */
function SpotlightOverlay({ targetRect }) {
  if (!targetRect) return null;

  const padding = 8;
  const borderRadius = 12;

  return (
    <div className="fixed inset-0 z-10000 pointer-events-none">
      <svg className="w-full h-full">
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={targetRect.left - padding}
              y={targetRect.top - padding}
              width={targetRect.width + padding * 2}
              height={targetRect.height + padding * 2}
              rx={borderRadius}
              ry={borderRadius}
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.5)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Highlight border around target */}
      <div
        className="absolute border-2 border-decisiv-blue rounded-xl animate-pulse"
        style={{
          top: targetRect.top - padding,
          left: targetRect.left - padding,
          width: targetRect.width + padding * 2,
          height: targetRect.height + padding * 2,
          boxShadow: '0 0 0 4px rgba(204, 120, 92, 0.3)'
        }}
      />
    </div>
  );
}

/**
 * Main FeatureTour component
 */
function FeatureTour({ isOpen, onClose, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  const currentStepData = TOUR_STEPS[currentStep];

  // Find and observe target element
  useEffect(() => {
    if (!isOpen || !currentStepData) return;

    const findTarget = () => {
      const target = document.querySelector(currentStepData.target);
      if (target) {
        const rect = target.getBoundingClientRect();
        setTargetRect(rect);
        setIsVisible(true);

        // Scroll target into view if needed
        if (rect.top < 0 || rect.bottom > window.innerHeight) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        // Target not found, skip to next step
        console.warn(`Tour target not found: ${currentStepData.target}`);
        if (currentStep < TOUR_STEPS.length - 1) {
          setCurrentStep(prev => prev + 1);
        }
      }
    };

    // Small delay to let DOM update
    const timer = setTimeout(findTarget, 100);

    // Update position on resize/scroll
    const handleUpdate = () => {
      const target = document.querySelector(currentStepData.target);
      if (target) {
        setTargetRect(target.getBoundingClientRect());
      }
    };

    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
    };
  }, [isOpen, currentStep, currentStepData]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleSkip();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrevious();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentStep]);

  const handleNext = useCallback(() => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
      setIsVisible(false);
    } else {
      handleComplete();
    }
  }, [currentStep]);

  const handlePrevious = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      setIsVisible(false);
    }
  }, [currentStep]);

  const handleSkip = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setCurrentStep(0);
    setIsVisible(false);
    if (onClose) onClose();
  }, [onClose]);

  const handleComplete = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setCurrentStep(0);
    setIsVisible(false);
    if (onComplete) onComplete();
    if (onClose) onClose();
  }, [onClose, onComplete]);

  if (!isOpen || !isVisible) return null;

  return createPortal(
    <>
      {/* Click blocker overlay */}
      <div
        className="fixed inset-0 z-9999"
        onClick={handleSkip}
      />

      {/* Spotlight effect */}
      <SpotlightOverlay targetRect={targetRect} />

      {/* Tooltip */}
      {targetRect && (
        <TourTooltip
          step={currentStepData}
          targetRect={targetRect}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onSkip={handleSkip}
          currentStep={currentStep}
          totalSteps={TOUR_STEPS.length}
        />
      )}
    </>,
    document.body
  );
}

/**
 * Hook to manage feature tour state
 */
export function useFeatureTour() {
  const [isOpen, setIsOpen] = useState(false);

  const startTour = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeTour = useCallback(() => {
    setIsOpen(false);
  }, []);

  const hasCompletedTour = useCallback(() => {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    isOpen,
    startTour,
    closeTour,
    hasCompletedTour,
    resetTour
  };
}

export default FeatureTour;
